import React, { useState, useEffect, useMemo } from 'react';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import './Recharge.css';
import { getPlanLabels, isCreditsPlan, isMembershipPlan } from '../../utils/plan';

const INITIAL_CREDITS_PAYMENT = {
  selectedPlan: null,
  qrCodeUrl: '',
  qrCodeDataUrl: '',
  outTradeNo: '',
  paymentStatus: '',
  loading: false,
  error: '',
};

const INITIAL_MEMBERSHIP_PAYMENT = {
  qrCodeUrl: '',
  qrCodeDataUrl: '',
  outTradeNo: '',
  paymentStatus: '',
  loading: false,
  error: '',
};

const Recharge = () => {
  const [showRechargeSuccess, setShowRechargeSuccess] = useState(false);
  const [showMembershipActive, setShowMembershipActive] = useState(false);
  const [plans, setPlans] = useState([]);
  const [benefits, setBenefits] = useState(null);
  const [rechargeType, setRechargeType] = useState('credits'); // credits | membership

  // 积分充值相关状态
  const [creditsPayment, setCreditsPayment] = useState({ ...INITIAL_CREDITS_PAYMENT });

  // 会员购买相关状态
  const [membershipPayment, setMembershipPayment] = useState({ ...INITIAL_MEMBERSHIP_PAYMENT });

  const membershipPlan = useMemo(
    () => plans.find((plan) => isMembershipPlan(plan)),
    [plans]
  );
  const membershipAvailable = Boolean(membershipPlan);

  const revokeObjectUrl = (url) => {
    if (url && typeof window !== 'undefined' && window.URL && window.URL.revokeObjectURL) {
      window.URL.revokeObjectURL(url);
    }
  };

  const fetchQRCodeImage = async (value, size = 180) => {
    const requestSize = Math.max(64, Math.min(size, 2048));
    const response = await fetch(
      `/api/utils/qrcode?data=${encodeURIComponent(value)}&size=${requestSize}`,
      {
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to generate QR code image');
    }

    const blob = await response.blob();
    if (typeof window === 'undefined' || !window.URL || !window.URL.createObjectURL) {
      throw new Error('Browser URL API is not available');
    }

    return window.URL.createObjectURL(blob);
  };

  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '余额充值', path: null }
  ];

  // 切换充值类型时清理支付状态
  const handleRechargeTypeChange = (type) => {
    setRechargeType(type);
    // 清理当前支付状态
    setCreditsPayment((prev) => {
      revokeObjectUrl(prev.qrCodeDataUrl);
      return { ...INITIAL_CREDITS_PAYMENT };
    });
    setMembershipPayment((prev) => {
      revokeObjectUrl(prev.qrCodeDataUrl);
      return { ...INITIAL_MEMBERSHIP_PAYMENT };
    });
  };

  useEffect(() => {
    fetchPlans();
    fetchBenefits();
  }, []);

  useEffect(() => {
    return () => {
      revokeObjectUrl(creditsPayment.qrCodeDataUrl);
      revokeObjectUrl(membershipPayment.qrCodeDataUrl);
    };
  }, [creditsPayment.qrCodeDataUrl, membershipPayment.qrCodeDataUrl]);

  // 积分支付轮询
  useEffect(() => {
    let statusPoller;
    if (rechargeType === 'credits' && creditsPayment.paymentStatus === 'WAITING' && creditsPayment.outTradeNo) {
      statusPoller = setInterval(async () => {
        try {
          const response = await fetch(`/api/billing/wechat/query?outTradeNo=${creditsPayment.outTradeNo}`, {
            method: 'GET',
            credentials: 'include',
          });
          const data = await response.json();

          if (data.code === 0) {
            if (data.data.tradeState === 'SUCCESS') {
              setCreditsPayment(prev => ({ ...prev, paymentStatus: 'SUCCESS' }));
              setShowRechargeSuccess(true);
              clearInterval(statusPoller);
              setTimeout(() => {
                fetchBenefits();
                setCreditsPayment(prev => {
                  revokeObjectUrl(prev.qrCodeDataUrl);
                  return { ...prev, qrCodeUrl: '', qrCodeDataUrl: '', selectedPlan: null };
                });
              }, 2000);
            } else if (data.data.tradeState === 'EXPIRED') {
              setCreditsPayment(prev => ({ ...prev, paymentStatus: 'EXPIRED' }));
              clearInterval(statusPoller);
            }
          }
        } catch (error) {
          console.error('Error polling credits payment status:', error);
        }
      }, 3000);
    }

    return () => {
      if (statusPoller) clearInterval(statusPoller);
    };
  }, [rechargeType, creditsPayment.paymentStatus, creditsPayment.outTradeNo]);

  // 会员支付轮询
  useEffect(() => {
    let statusPoller;
    if (rechargeType === 'membership' && membershipPayment.paymentStatus === 'WAITING' && membershipPayment.outTradeNo) {
      statusPoller = setInterval(async () => {
        try {
          const response = await fetch(`/api/billing/wechat/query?outTradeNo=${membershipPayment.outTradeNo}`, {
            method: 'GET',
            credentials: 'include',
          });
          const data = await response.json();

          if (data.code === 0) {
            if (data.data.tradeState === 'SUCCESS') {
              setMembershipPayment(prev => ({ ...prev, paymentStatus: 'SUCCESS' }));
              setShowMembershipActive(true);
              clearInterval(statusPoller);
              setTimeout(() => {
                setMembershipPayment(prev => {
                  revokeObjectUrl(prev.qrCodeDataUrl);
                  return { ...prev, qrCodeUrl: '', qrCodeDataUrl: '' };
                });
              }, 2000);
            } else if (data.data.tradeState === 'EXPIRED') {
              setMembershipPayment(prev => ({ ...prev, paymentStatus: 'EXPIRED' }));
              clearInterval(statusPoller);
            }
          }
        } catch (error) {
          console.error('Error polling membership payment status:', error);
        }
      }, 3000);
    }

    return () => {
      if (statusPoller) clearInterval(statusPoller);
    };
  }, [rechargeType, membershipPayment.paymentStatus, membershipPayment.outTradeNo]);

  const fetchPlans = async () => {
    try {
      const response = await fetch('/api/billing/plans');
      const data = await response.json();

      if (data.code === 0) {
        setPlans(data.data.items || []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchBenefits = async () => {
    try {
      const response = await fetch('/api/billing/benefits', {
        credentials: 'include',
      });
      const data = await response.json();

      if (data.code === 0) {
        setBenefits(data.data);
      }
    } catch (error) {
      console.error('Error fetching benefits:', error);
    }
  };

  const handlePlanSelect = async (plan) => {
    if (creditsPayment.loading) return;

    setCreditsPayment((prev) => {
      revokeObjectUrl(prev.qrCodeDataUrl);
      return {
        ...prev,
        selectedPlan: plan,
        qrCodeUrl: '',
        qrCodeDataUrl: '',
        outTradeNo: '',
        loading: true,
        error: '',
        paymentStatus: '',
      };
    });

    try {
      const response = await fetch('/api/billing/wechat/qrcode_url', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planId: plan.id }),
      });

      const data = await response.json();

      if (data.code === 0) {
        let qrImageUrl = '';
        try {
          qrImageUrl = await fetchQRCodeImage(data.data.qrCodeUrl);
        } catch (imageError) {
          console.error('Failed to generate credits QR code:', imageError);
          setCreditsPayment((prev) => {
            revokeObjectUrl(prev.qrCodeDataUrl);
            return {
              ...prev,
              qrCodeUrl: '',
              qrCodeDataUrl: '',
              outTradeNo: '',
              loading: false,
              paymentStatus: 'ERROR',
              error: '二维码生成失败，请稍后重试',
            };
          });
          return;
        }

        setCreditsPayment((prev) => {
          if (prev.qrCodeDataUrl && prev.qrCodeDataUrl !== qrImageUrl) {
            revokeObjectUrl(prev.qrCodeDataUrl);
          }
          return {
            ...prev,
            qrCodeUrl: data.data.qrCodeUrl,
            qrCodeDataUrl: qrImageUrl,
            outTradeNo: data.data.outTradeNo,
            paymentStatus: 'WAITING',
            loading: false,
          };
        });
      } else {
        setCreditsPayment((prev) => {
          revokeObjectUrl(prev.qrCodeDataUrl);
          return {
            ...prev,
            qrCodeUrl: '',
            qrCodeDataUrl: '',
            outTradeNo: '',
            error: data.msg || '生成支付二维码失败',
            paymentStatus: 'ERROR',
            loading: false,
          };
        });
      }
    } catch (error) {
      console.error('Error generating credits payment QR code:', error);
      setCreditsPayment((prev) => {
        revokeObjectUrl(prev.qrCodeDataUrl);
        return {
          ...prev,
          qrCodeUrl: '',
          qrCodeDataUrl: '',
          outTradeNo: '',
          error: '网络错误，请稍后重试',
          paymentStatus: 'ERROR',
          loading: false,
        };
      });
    }
  };

  const handleMembershipPurchase = async () => {
    if (membershipPayment.loading) return;

    if (!membershipPlan) {
      setMembershipPayment((prev) => ({
        ...prev,
        error: '暂无可用闲时会员套餐',
        paymentStatus: 'ERROR',
      }));
      return;
    }

    const planId = membershipPlan.id;

    setMembershipPayment((prev) => {
      revokeObjectUrl(prev.qrCodeDataUrl);
      return {
        ...prev,
        qrCodeUrl: '',
        qrCodeDataUrl: '',
        outTradeNo: '',
        loading: true,
        error: '',
        paymentStatus: '',
      };
    });

    try {
      const response = await fetch('/api/billing/wechat/qrcode_url', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId,
          description: '闲时会员 - 无限合成视频'
        }),
      });

      const data = await response.json();

      if (data.code === 0) {
        let qrImageUrl = '';
        try {
          qrImageUrl = await fetchQRCodeImage(data.data.qrCodeUrl);
        } catch (imageError) {
          console.error('Failed to generate membership QR code:', imageError);
          setMembershipPayment((prev) => {
            revokeObjectUrl(prev.qrCodeDataUrl);
            return {
              ...prev,
              qrCodeUrl: '',
              qrCodeDataUrl: '',
              outTradeNo: '',
              loading: false,
              paymentStatus: 'ERROR',
              error: '二维码生成失败，请稍后重试',
            };
          });
          return;
        }

        setMembershipPayment((prev) => {
          if (prev.qrCodeDataUrl && prev.qrCodeDataUrl !== qrImageUrl) {
            revokeObjectUrl(prev.qrCodeDataUrl);
          }
          return {
            ...prev,
            qrCodeUrl: data.data.qrCodeUrl,
            qrCodeDataUrl: qrImageUrl,
            outTradeNo: data.data.outTradeNo,
            paymentStatus: 'WAITING',
            loading: false,
          };
        });
      } else {
        setMembershipPayment((prev) => {
          revokeObjectUrl(prev.qrCodeDataUrl);
          return {
            ...prev,
            qrCodeUrl: '',
            qrCodeDataUrl: '',
            outTradeNo: '',
            error: data.msg || '生成支付二维码失败',
            paymentStatus: 'ERROR',
            loading: false,
          };
        });
      }
    } catch (error) {
      console.error('Error generating membership payment QR code:', error);
      setMembershipPayment((prev) => {
        revokeObjectUrl(prev.qrCodeDataUrl);
        return {
          ...prev,
          qrCodeUrl: '',
          qrCodeDataUrl: '',
          outTradeNo: '',
          error: '网络错误，请稍后重试',
          paymentStatus: 'ERROR',
          loading: false,
        };
      });
    }
  };

  const formatPrice = (price) => {
    return parseFloat(price).toFixed(2);
  };

  const membershipPlanPrice = membershipPlan ? formatPrice(membershipPlan.price) : '--';
  const membershipFallbackQR = 'https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(\'https://work.weixin.qq.com/ct/wcde3d12f7814a242db34db2e2584a12fdc9\')}';

  const getCurrentBalance = () => {
    if (benefits) {
      return (benefits.total - benefits.totalUsed).toLocaleString();
    }
    return '0';
  };

  return (
    <div className="recharge-page">
      <Breadcrumb items={breadcrumbItems} />

      <h1 className="page-title">余额充值</h1>

      <div className="recharge-card">
        {/* Success banners */}
        {showRechargeSuccess && (
          <div className="success-banner">
            <div className="success-icon" />
            <div className="success-texts">
              <div className="success-title">已成功充值积分</div>
              <div className="success-sub">成功充值 60000 积分，当前积分余额为 82288</div>
            </div>
          </div>
        )}

        {showMembershipActive && (
          <div className="success-banner">
            <div className="success-icon" />
            <div className="success-texts">
              <div className="success-title">已成功购买闲时会员</div>
              <div className="success-sub">会员有效期至 2025-09-19 ，查询使用说明</div>
            </div>
          </div>
        )}

        <p className="intro-text">
          你当前的积分余额为 {getCurrentBalance()}，请根据诉求选择适合你的购买方案
        </p>

        <div className="radio-group outlined">
          <button
            className={`radio-option left ${rechargeType === 'credits' ? 'active' : ''}`}
            onClick={() => handleRechargeTypeChange('credits')}
          >
            立刻充值积分
          </button>
          <button
            className={`radio-option right ${rechargeType === 'membership' ? 'active' : ''}`}
            onClick={() => handleRechargeTypeChange('membership')}
          >
            购买闲时会员，无限合成视频
          </button>
        </div>

        {/* Error messages */}
        {creditsPayment.error && rechargeType === 'credits' && (
          <div className="error-message">
            {creditsPayment.error}
          </div>
        )}

        {membershipPayment.error && rechargeType === 'membership' && (
          <div className="error-message">
            {membershipPayment.error}
          </div>
        )}

        {/* Plans grid - only show for credits */}
        {rechargeType === 'credits' && (
          <div className="plans-grid">
            {plans
              .filter((plan) => isCreditsPlan(plan))
              .map((plan, index) => {
                const hasDiscount = parseFloat(plan.originalPrice) > parseFloat(plan.price);
                const discountPercentage = hasDiscount
                  ? Math.round((parseFloat(plan.price) / parseFloat(plan.originalPrice)) * 10)
                  : 0;
                const planLabels = getPlanLabels(plan);
                const durationNumeric = Number.parseInt(plan.duration, 10);
                const totalCredits = Number.isFinite(durationNumeric) ? durationNumeric : 0;
                const durationValue = totalCredits.toLocaleString();
                const videosCount = totalCredits > 0 ? Math.floor(totalCredits / 600) : 0;
                const videosCountLabel = videosCount.toLocaleString();

                return (
                  <div
                    key={plan.id}
                    className={`plan-card ${index === 0 ? 'primary' : ''} ${creditsPayment.selectedPlan?.id === plan.id ? 'selected' : ''} ${creditsPayment.loading && creditsPayment.selectedPlan?.id === plan.id ? 'loading' : ''}`}
                    onClick={() => handlePlanSelect(plan)}
                    style={{ cursor: creditsPayment.loading ? 'not-allowed' : 'pointer' }}
                  >
                    <div className="plan-header">
                      <div>
                        <div className={`plan-amount ${index === 0 ? '' : 'dark'}`}>
                          {durationValue}
                          <span className="plan-unit">{planLabels.tag}</span>
                        </div>
                        <div className="plan-description">
                          可用于生成{videosCountLabel}分钟视频
                        </div>
                      </div>
                      <div className={`plan-label ${index === 0 ? '' : 'dark'}`}>
                        {hasDiscount && `${discountPercentage}折`}
                      </div>
                    </div>

                    <div className="plan-pricing">
                      <span className={`plan-price ${index === 0 ? '' : 'dark'}`}>¥{formatPrice(plan.price)}</span>
                      {hasDiscount && (
                        <span className="plan-original">原价¥{formatPrice(plan.originalPrice)}</span>
                      )}
                    </div>

                    {creditsPayment.loading && creditsPayment.selectedPlan?.id === plan.id && (
                      <div className="loading-overlay">
                        <div className="loading-spinner"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            {plans.filter((plan) => isCreditsPlan(plan)).length === 0 && (
              <div className="plan-card dashed">
                <div className="contact-text">暂无可用套餐</div>
              </div>
            )}
          </div>
        )}

        {/* Credits Payment section */}
        {rechargeType === 'credits' && (creditsPayment.qrCodeUrl || creditsPayment.paymentStatus === 'SUCCESS' || creditsPayment.paymentStatus === 'EXPIRED' || creditsPayment.paymentStatus === 'ERROR') && (
          <div className="pay-section">
            <div className="qr-block">
              {creditsPayment.paymentStatus === 'WAITING' && (
                creditsPayment.qrCodeDataUrl ? (
                  <div
                    className="qr-image"
                    style={{
                      backgroundImage: `url(${creditsPayment.qrCodeDataUrl})`,
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'contain'
                    }}
                  />
                ) : (
                  <div className="qr-image qr-placeholder">二维码生成中...</div>
                )
              )}

              {creditsPayment.paymentStatus === 'SUCCESS' && (
                <div className="payment-status success">
                  <div className="payment-status-icon">✅</div>
                  <div className="payment-status-text">支付成功！</div>
                </div>
              )}

              {creditsPayment.paymentStatus === 'EXPIRED' && (
                <div className="payment-status expired">
                  <div className="payment-status-icon">⏰</div>
                  <div className="payment-status-text">支付二维码已过期</div>
                  <button
                    className="retry-button"
                    onClick={() => creditsPayment.selectedPlan && handlePlanSelect(creditsPayment.selectedPlan)}
                  >
                    重新生成
                  </button>
                </div>
              )}

              {creditsPayment.paymentStatus === 'ERROR' && (
                <div className="payment-status error">
                  <div className="payment-status-icon">❌</div>
                  <div className="payment-status-text">生成支付二维码失败</div>
                  <button
                    className="retry-button"
                    onClick={() => creditsPayment.selectedPlan && handlePlanSelect(creditsPayment.selectedPlan)}
                  >
                    重试
                  </button>
                </div>
              )}
            </div>

            <div className="pay-info">
              <div className="amount-line">
                <span className="amount-label">应付金额</span>
                <span className="amount-value">
                  ¥{creditsPayment.selectedPlan ? formatPrice(creditsPayment.selectedPlan.price) : '0.00'}
                </span>
              </div>

              <div className="note-text">
                {creditsPayment.paymentStatus === 'WAITING' && (
                  <>
                    <span>请使用 微信 扫码支付</span>
                    <span>支付即视为你同意<a href="https://uvetjcpbqd.feishu.cn/wiki/QXFBw6znQix2uCkvcNxc3Makncg" target="_blank" rel="noopener noreferrer">深锶数字人积分充值协议</a></span>
                    <span style={{ color: '#40a9ff' }}>支付状态将自动更新，请勿关闭页面</span>
                  </>
                )}
                {creditsPayment.paymentStatus === 'SUCCESS' && (
                  <span style={{ color: '#52c41a' }}>支付成功，积分已到账！</span>
                )}
                {(creditsPayment.paymentStatus === 'EXPIRED' || creditsPayment.paymentStatus === 'ERROR') && (
                  <span>支付遇到问题，请重新尝试或联系客服</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Membership banner */}
        {rechargeType === 'membership' && membershipAvailable && (
          <div
            className="plan-banner clickable"
            onClick={() => handleMembershipPurchase()}
            style={{ cursor: 'pointer' }}
          >
            <div className="banner-left" />
            <div className="banner-bottom" />
            <div className="banner-title">无限合成视频</div>
            <div className="banner-desc">使用服务区空闲的资源合成，性价比高，适用于大量视频</div>
            <div className="banner-price">¥{membershipPlanPrice}/月</div>
          </div>
        )}

        {rechargeType === 'membership' && !membershipAvailable && (
          <div className="membership-fallback">
            <div className="fallback-message">仅支持通过API调用，可详询服务经理</div>
            <div className="fallback-qr">
              <img src={membershipFallbackQR} alt="联系服务经理二维码" />
            </div>
          </div>
        )}

        {/* Membership Payment section */}
        {rechargeType === 'membership' && membershipAvailable && (membershipPayment.qrCodeUrl || membershipPayment.paymentStatus === 'SUCCESS' || membershipPayment.paymentStatus === 'EXPIRED' || membershipPayment.paymentStatus === 'ERROR') && (
          <div className="pay-section">
            <div className="qr-block">
              {membershipPayment.paymentStatus === 'WAITING' && (
                membershipPayment.qrCodeDataUrl ? (
                  <div
                    className="qr-image"
                    style={{
                      backgroundImage: `url(${membershipPayment.qrCodeDataUrl})`,
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'contain'
                    }}
                  />
                ) : (
                  <div className="qr-image qr-placeholder">二维码生成中...</div>
                )
              )}

              {membershipPayment.paymentStatus === 'SUCCESS' && (
                <div className="payment-status success">
                  <div className="payment-status-icon">✅</div>
                  <div className="payment-status-text">支付成功！</div>
                </div>
              )}

              {membershipPayment.paymentStatus === 'EXPIRED' && (
                <div className="payment-status expired">
                  <div className="payment-status-icon">⏰</div>
                  <div className="payment-status-text">支付二维码已过期</div>
                  <button
                    className="retry-button"
                    onClick={() => handleMembershipPurchase()}
                  >
                    重新生成
                  </button>
                </div>
              )}

              {membershipPayment.paymentStatus === 'ERROR' && (
                <div className="payment-status error">
                  <div className="payment-status-icon">❌</div>
                  <div className="payment-status-text">生成支付二维码失败</div>
                  <button
                    className="retry-button"
                    onClick={() => handleMembershipPurchase()}
                  >
                    重试
                  </button>
                </div>
              )}
            </div>

            <div className="pay-info">
              <div className="amount-line">
                <span className="amount-label">应付金额</span>
                <span className="amount-value">¥{membershipPlanPrice}</span>
              </div>

              <div className="note-text">
                {membershipPayment.paymentStatus === 'WAITING' && (
                  <>
                    <span>请使用 微信 扫码支付</span>
                    <span>支付即视为你同意<a href="https://uvetjcpbqd.feishu.cn/wiki/QXFBw6znQix2uCkvcNxc3Makncg" target="_blank" rel="noopener noreferrer">深锶数字人积分充值协议</a></span>
                    <span style={{ color: '#40a9ff' }}>支付状态将自动更新，请勿关闭页面</span>
                  </>
                )}
                {membershipPayment.paymentStatus === 'SUCCESS' && (
                  <span style={{ color: '#52c41a' }}>支付成功，会员已激活！</span>
                )}
                {(membershipPayment.paymentStatus === 'EXPIRED' || membershipPayment.paymentStatus === 'ERROR') && (
                  <span>支付遇到问题，请重新尝试或联系客服</span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="wechat-icon" aria-hidden="true" />
      </div>
    </div>
  );
};

export default Recharge;